import { FC } from "preact/compat";
import { useComputed, useSignal } from "@preact/signals";
import { useNavigate } from "react-router-dom";
import { Employee, useApp } from "~/core";
import { me } from "~/core/store";
import { Button, TextField } from "~/components";
import { EmployeeInput } from "~/organisms/EmployeeInput";
import useBudgetApproval from "~/routes/project/useBudgetApproval";
import { ApprovalButtonProps } from "./interfaces";
import SendIcon from "@material-design-icons/svg/outlined/send.svg";
import Loader from "~/assets/loader.svg";

const ApprovalButton: FC<ApprovalButtonProps> = ({
    budget,
    project,
    params,
}) => {
    const { api, snack } = useApp();
    const approval = useBudgetApproval(budget.id);
    const navigate = useNavigate();
    const onlyExternalApproval =
        api.settings.company.store.value.budget.only_external_approval;
    const to = approval.external.approved
        ? null
        : onlyExternalApproval || approval.internal.sent
          ? "client"
          : "internal";
    const sending = useSignal(false);
    const selected = useSignal<Employee | null>(null);
    const defaultApprover = useComputed(() =>
        api.employee.getByUsername(project.client_manager),
    );
    const approver = useComputed(() => selected.value ?? defaultApprover.value);
    const email = useSignal("");
    const disabled =
        to === "internal" ? !approver.value : email.value.trim() === "";

    const handleSendInternal = async () => {
        if (sending.peek()) {
            return;
        }

        sending.value = true;

        try {
            const employee = approver.peek();

            if (!employee) {
                snack.error(
                    "No approver selected",
                    "Please select an employee to approve this budget",
                );
                return;
            }

            await api.budget.print(budget._id, { save: true, ...params });
            await api.budget.sendInternal({
                id: budget._id,
                approver: employee._id,
            });

            snack.info("OK", "Budget sent for internal approval");
            navigate(me(`/projects/${budget.project.toString()}/general`));
        } catch (err: unknown) {
            const message =
                err instanceof Error ? err.message : "Unknown error";

            snack.error("Failed to send budget", message);
            console.log(err);
        }

        sending.value = false;
    };

    const handleSendClient = async () => {
        try {
            await api.budget.sendExternal({
                id: budget._id,
                email: email.peek(),
            });
            snack.info("OK", "Budget sent to client for approval");
            navigate(me(`/projects/${budget.project.toString()}/general`));
        } catch (err: unknown) {
            const message =
                err instanceof Error ? err.message : "Unknown error";

            snack.error("Failed to send budget", message);
            console.log(err);
        }
    };

    const handleClick = () => {
        if (to === "internal") {
            handleSendInternal();
        } else if (to === "client") {
            handleSendClient();
        }
    };

    return (
        <div className="flex flex-col gap-y-2">
            {to === "internal" && (
                <EmployeeInput
                    label="Approver"
                    variant="classic"
                    value={approver.value}
                    direction="up"
                    onChange={(employee) => (selected.value = employee)}
                />
            )}
            {to === "client" && (
                <TextField
                    label="Client Email"
                    value={email.value}
                    onInput={(e) => (email.value = e.currentTarget.value)}
                />
            )}
            {to && (
                <Button
                    variant="success"
                    disabled={disabled || sending.value}
                    onClick={handleClick}
                    className="gap-x-2 justify-center"
                >
                    <span>
                        {to === "client" ? "Send to Client" : "Send to Manager"}
                    </span>
                    {sending.value ? (
                        <Loader width="24" height="24" className="text-white" />
                    ) : (
                        <SendIcon width="24" height="24" />
                    )}
                </Button>
            )}
        </div>
    );
};

export default ApprovalButton;
